import React from 'react'
import classnames from 'classnames'
import { TenantRegistration } from '../../../../types/registration'
import { isNgomOrVnemTenantName } from '../constants'

/**
 * Single source of truth for the small attribute badges (National / NGOM / Incomplete /
 * Duplicate / Archived) shown next to a registration record. Renders the same set of badges
 * in the same order on the list view (compact) and the record-edit panel header (slightly
 * larger). Update colors / labels here only — both call sites pick up the change.
 *
 * If you need to add a new badge type, add it to {@link BADGE_DEFINITIONS} and (if it isn't
 * already a field on the record) extend {@link RecordAttributeBadgesProps['record']}.
 */

type BadgeKind = 'NATIONAL' | 'NGOM' | 'INCOMPLETE' | 'DUPLICATE' | 'ARCHIVED'

type BadgeDefinition = {
    label: string
    /** Color classes only; sizing/weight is applied by the component based on `size`. */
    classes: string
    /** Optional leading icon — only rendered when `size === 'detail'` (compact list cells stay icon-free). */
    icon?: React.ReactNode
}

const WarningTriangleIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
    </svg>
)

const BADGE_DEFINITIONS: Record<BadgeKind, BadgeDefinition> = {
    NATIONAL: {
        label: 'National',
        classes: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
    },
    NGOM: {
        label: 'NGOM',
        classes: 'bg-yellow-500 dark:bg-yellow-500 text-yellow-900 dark:text-yellow-900',
    },
    INCOMPLETE: {
        label: 'Incomplete',
        classes: 'bg-indigo-500 text-white',
        icon: <WarningTriangleIcon />,
    },
    DUPLICATE: {
        label: 'Duplicate',
        classes: 'bg-rose-500 text-white',
    },
    ARCHIVED: {
        label: 'Archived',
        classes: 'bg-amber-600 text-white',
    },
}

export interface RecordAttributeBadgesProps {
    record: Pick<
        TenantRegistration,
        | 'isNational'
        | 'isComplete'
        | 'archivedAt'
        | 'hasDuplicateSalesforceAccountId'
        | 'hasDuplicateUtilityAccountAtProperty'
        | 'hasDuplicateContactEmailAtProperty'
        | 'duplicateReasons'
        | 'tenant'
    >
    /**
     * `'list'`: compact (px-1.5, font-medium) — used in table cells.
     * `'detail'`: slightly larger (px-2, font-semibold) — used in panel headers.
     */
    size: 'list' | 'detail'
    /** Optional wrapper className override for layout (defaults to flex-wrap row with gap). */
    className?: string
}

const NGOM_TITLE = 'Detected from business name (NGOM/VNEM). TESA and PMA are not required for these accounts.'
const NATIONAL_TITLE = 'National account. TESA and PMA are not required for these accounts.'

function duplicateTooltip(record: RecordAttributeBadgesProps['record']): string {
    const reasons = record.duplicateReasons ?? []
    const parts: string[] = []
    if (reasons.includes('SALESFORCE_ACCOUNT') || record.hasDuplicateSalesforceAccountId) {
        const sfId = record.tenant?.salesforceAccountId
        parts.push(sfId ? `Salesforce account ${sfId}` : 'Salesforce account')
    }
    if (reasons.includes('UTILITY_ACCOUNT') || record.hasDuplicateUtilityAccountAtProperty) {
        parts.push('utility account number at this property')
    }
    if (reasons.includes('CONTACT_EMAIL') || record.hasDuplicateContactEmailAtProperty) {
        parts.push('contact email at this property')
    }
    if (parts.length === 0) {
        return 'Multiple records may represent the same tenant'
    }
    return `Multiple active records share the same ${parts.join(' and ')}`
}

export const RecordAttributeBadges: React.FC<RecordAttributeBadgesProps> = ({ record, size, className }) => {
    const isNgom = isNgomOrVnemTenantName(record.tenant?.name)
    const isDuplicate =
        record.hasDuplicateSalesforceAccountId === true ||
        record.hasDuplicateUtilityAccountAtProperty === true ||
        record.hasDuplicateContactEmailAtProperty === true
    const isArchived = record.archivedAt != null

    // Order is intentional: identifying attributes (National, NGOM) first, then state attributes
    // (Incomplete, Duplicate, Archived). Keep both call sites visually consistent.
    const items: Array<{ kind: BadgeKind; title?: string }> = []
    if (record.isNational === true) items.push({ kind: 'NATIONAL', title: NATIONAL_TITLE })
    if (isNgom) items.push({ kind: 'NGOM', title: NGOM_TITLE })
    if (record.isComplete === false) items.push({ kind: 'INCOMPLETE' })
    if (isDuplicate) {
        items.push({
            kind: 'DUPLICATE',
            title: duplicateTooltip(record),
        })
    }
    if (isArchived) items.push({ kind: 'ARCHIVED' })

    if (items.length === 0) return null

    const sizeClasses = size === 'list' ? 'px-1.5 py-0.5 text-xs font-medium' : 'px-2 py-0.5 text-xs font-semibold'

    return (
        <div className={className ?? 'flex flex-wrap gap-1'}>
            {items.map(({ kind, title }) => {
                const def = BADGE_DEFINITIONS[kind]
                const showIcon = size === 'detail' && def.icon != null
                return (
                    <span
                        key={kind}
                        className={classnames('inline-flex items-center rounded', sizeClasses, def.classes, {
                            'gap-1': showIcon,
                        })}
                        title={title}
                    >
                        {showIcon ? def.icon : null}
                        {def.label}
                    </span>
                )
            })}
        </div>
    )
}
